import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAppSelector } from '../../hooks';
import Panel from '../../components/Panel/Panel';
import { getItemName, getItemSrc } from '../../resources/Items';
import { toServer, fromServer, cancelFromServer } from '../../util/ServerSocket';
import './ItemMall.css';
import Events from '../../util/EventEmitter';

const categories = [['popular','ยอดนิยม'],['food','อาหาร'],['gem','เจม'],['pet','สัตว์เลี้ยง'],['vip','VIP'],['other','อื่น ๆ']];
const asset = (path: string) => `${process.env.REACT_APP_CDN_PATH ?? ''}${path}`;
type Product = { iid: number; category: string; price: number; basePrice: number };
function request(event: string, data = {}): Promise<any> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error('ไม่ได้รับคำตอบ กรุณาลองอีกครั้ง')), 10000);
    toServer(event, data, (reply: any) => { window.clearTimeout(timer); resolve(reply); });
  });
}
export function ItemMall({ mode = 'coupons' }: { mode?: 'coupons' | 'gold' }) {
  const gold = mode === 'gold';
  const shopName = gold ? 'ร้านค้าโกลด์' : 'ร้านค้านินจาลับ';
  const currencyLabel = gold ? 'โกลด์' : 'คูปอง';
  const scene = useAppSelector(s => s.scene.scene), fight = useAppSelector(s => s.fight.fight);
  const villageBack = useAppSelector(s => s.ui.villageBack);
  const currency = useAppSelector(s => s.character.currency);
  const [open, setOpen] = useState(false), [category, setCategory] = useState('popular');
  const [products, setProducts] = useState<Product[]>([]), [sequence, setSequence] = useState(0);
  const [busy, setBusy] = useState(false), [message, setMessage] = useState('');
  const [selected, setSelected] = useState<Product | null>(null), [quantity, setQuantity] = useState(1);
  const pending = useRef(false), purchase = useRef<any>();
  const load = async () => {
    const info = await request(gold ? 'goldMallInfo' : 'itemMallInfo');
    if (info.error) throw new Error(info.error);
    setProducts(info.products); setSequence(info.sequence);
    setSelected(current => current ? info.products.find((row: Product) => row.iid === current.iid) ?? null : null);
  };
  async function show() {
    setOpen(true); setBusy(true); setMessage('');
    try { await load(); } catch (e) { setMessage((e as Error).message); } finally { setBusy(false); }
  }
  async function buy() {
    if (!selected || pending.current) return;
    pending.current = true; setBusy(true); setMessage('');
    purchase.current ??= { requestId: `${Date.now()}-${Math.random()}`, iid: selected.iid, quantity, cost: selected.price * quantity, sequence };
    try {
      const reply = await request(gold ? 'goldMallBuy' : 'itemMallBuy', purchase.current);
      purchase.current = undefined;
      if (reply.error) { setMessage(reply.error); await load(); return; }
      setMessage(`ซื้อ ${getItemName(reply.iid)} ×${reply.quantity} สำเร็จ ส่งเข้ากระเป๋าแล้ว`);
      setSelected(null); await load();
    } catch (e) { setMessage((e as Error).message); }
    finally { pending.current = false; setBusy(false); }
  }
  const available = (gold ? [111,211,311,411,511].includes(Number(scene)) : Number(scene) === 2601) && !fight && !villageBack;
  useEffect(() => { if (!available && !pending.current) setOpen(false); }, [available]);
  useEffect(() => {
    const refresh = () => { if (open) load().catch(e => setMessage(e.message)); };
    const event = gold ? 'goldMallConfigChanged' : 'itemMallConfigChanged';
    fromServer(event, refresh);
    return () => { cancelFromServer(event, refresh); };
  }, [open, gold]);
  useEffect(() => {
    const listener = Events.on(gold ? 'openGoldMall' : 'openItemMall', () => { if (available && !pending.current) show(); });
    return () => Events.off(listener);
  }, [available, gold]);
  return <>
    {open && createPortal(<div className="item-mall-backdrop" role="dialog" aria-modal="true" aria-label={shopName}>
      <Panel name={shopName} moveable={false} onClose={() => { if (!busy) setOpen(false); }} style={{ position:'fixed',left:'50%',top:'50%',transform:'translate(-50%,-50%)',zIndex:31001 }}>
        <div className="item-mall">
          <nav aria-label="หมวดสินค้า">{categories.map(([id,name]) => <button key={id} className={category === id ? 'selected' : ''} onClick={() => { if (!selected) setCategory(id); }}>{name}</button>)}</nav>
          <div className="item-mall-body"><main>
            <div className="item-mall-banner" style={{ backgroundImage: `url(${asset(`ui/UIResource/PropMaketplace/${gold ? 'L1' : 'L'}.png`)})` }} />
            <div className="item-mall-heading"><b>{categories.find(([id]) => id === category)?.[1]}</b><span>ราคาต่อชิ้น · {currencyLabel}</span></div>
            <div className="item-mall-products">{products.filter(row => row.category === category).map(row => <article key={row.iid}>
              <h3>{getItemName(row.iid)}</h3><div className="item-mall-product"><img src={asset(getItemSrc({ iid:row.iid }))} alt="" /><div>
                {row.price !== row.basePrice && <small className="item-mall-discount">VIP −20% <del>{row.basePrice.toLocaleString()}</del></small>}
                <strong>{row.price.toLocaleString()} {currencyLabel}</strong><button disabled={busy || !!selected} onClick={() => { setSelected(row); setQuantity(1); setMessage(''); }}>ซื้อ</button>
              </div></div></article>)}</div>
            {!products.length && !busy && <p>ยังไม่มีสินค้า <button onClick={() => load().catch(e => setMessage(e.message))}>โหลดใหม่</button></p>}
          </main><aside style={{ backgroundImage:`url(${asset('ui/UIResource/PropMaketplace/mm.png')})` }}><div className="item-mall-wallet"><span>โกลด์ <b>{(currency.gold ?? 0).toLocaleString()}</b></span><span>คูปอง <b>{currency.giftCertificates.toLocaleString()}</b></span></div></aside></div>
          <p className="item-mall-message" role="status">{busy ? 'กำลังดำเนินการ…' : message || 'ซื้อสินค้าแล้วรับเข้ากระเป๋าทันที · บัตร VIP ต้องกดใช้ในกระเป๋า'}</p>
          {selected && <div className="item-mall-confirm" role="dialog" aria-label="ยืนยันซื้อสินค้า"><h3>{getItemName(selected.iid)}</h3>
            <label>จำนวน <input aria-label="จำนวนสินค้า" type="number" min={1} max={99} value={quantity} disabled={busy || !!purchase.current} onChange={e => setQuantity(Math.min(99,Math.max(1,Number(e.target.value) || 1)))} /></label>
            <p>รวม {(selected.price * quantity).toLocaleString()} {currencyLabel}</p>
            <div><button disabled={busy || !!purchase.current} onClick={() => setSelected(null)}>ยกเลิก</button><button disabled={busy} onClick={buy}>{purchase.current ? 'ตรวจสอบ / ลองอีกครั้ง' : 'ยืนยันซื้อ'}</button></div>
            {message && <p role="alert">{message}</p>}
          </div>}
        </div>
      </Panel>
    </div>, document.body)}
  </>;
}
